import { useEffect, useState, useRef } from 'react';
import PayPalSubscriptionButton from '../paypalSubscriptionButton';
import HowToBuymeacoffeeDialog from './howToBuymeacoffee';
import request from '../../lib/request';
import Support from './support';

const PLAN_TYPE = {
  YEARLY: 'YEARLY',
  MONTHLY: 'MONTHLY',
};

const PLAN_TITLE = {
  '3-Month Access': '3-Month Access',
  '1-Year Access': '1-Year Access',
};

const offers = [
  'Access to all challenges',
  'Progress tracking',
  'Community support',
  'AI meal tracking',
  'Goal setting tools',
];

const Policy = () => {
  return (
    <div>
      <p className='text-xs text-center opacity-50 text-white'>
        This is a one-time payment subscription. You will be charged only once,
        with no recurring fees. All payments are non-refundable. After purchase,
        you will retain access for the full duration of the selected plan.
      </p>
      <div className='flex gap-1 justify-center text-xs'>
        <a
          href='https://web.thechamps.app/legal/privacy-policy.html'
          target='_blank'
          className='text-blue-600'
        >
          Privacy Policy
        </a>
        <span className='text-white opacity-50'>|</span>
        <a
          href='https://web.thechamps.app/legal/terms.html'
          target='_blank'
          className='text-blue-600'
        >
          Terms and Conditions
        </a>
      </div>
    </div>
  );
};

const Plans = ({ selectedPlan, handleSelectedPlan }) => {
  const [plans, setPlans] = useState([
    {
      currency: 'USD',
      description: '3-Month Access Subscription',
      period: '3-Month',
      price: 27,
      title: '3-Month Access',
      link: 'https://buymeacoffee.com/davidpark/e/423472',
    },
    {
      currency: 'USD',
      description: '1-Year Access Subscription',
      period: '1-Year',
      price: 81,
      title: '1-Year Access',
      link: 'https://buymeacoffee.com/davidpark/e/423481',
      discount: 25,
    },
  ]);

  return (
    <div className='w-full space-y-4'>
      {plans.map((plan, i) => (
        <div
          key={i}
          className={`text-sm w-full  rounded-xl px-4 py-2 ${
            selectedPlan?.title === plan.title
              ? 'bg-accent/20 border-accent border-2 text-accent'
              : 'bg-neutral-100 border-2 border-neutral-100'
          }`}
          onClick={() => handleSelectedPlan(plan)}
        >
          <div
            className={`flex items-center gap-1 ${
              selectedPlan?.title === plan.title ? '' : 'text-tertiary'
            }`}
          >
            <div className='font-bold'>{plan.title}</div>
            {plan.discount && (
              <div className='font-bold'>{`(${plan.discount}% off)`}</div>
            )}
          </div>
          <div className='flex justify-between items-center'>
            <div>
              ${plan.price} / {plan.period}{' '}
              {plan.discount && <span className='line-through'>($108)</span>}
            </div>
            <div
              className={`${
                selectedPlan?.title === plan.title ? 'opacity-90' : 'opacity-40'
              }`}
            >
              $
              {plan.title === PLAN_TITLE['1-Year Access']
                ? (plan.price / 52).toFixed(2)
                : (plan.price / 12).toFixed(2)}{' '}
              / week
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const words = [
  ["Don't wait for motivation — build it.", 'Unlock your premium journey.'],
  ['This is more than a subscription', "— it's your commitment"],
  ["Let's build your momentum."],
];

const PayDialog = ({ dialogRef, closeDialog, title = '', refreshUser }) => {
  const supportDialogRef = useRef(null);
  const howToBuymeacoffeeDialogRef = useRef(null);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [wording, setWording] = useState([]);

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * words.length);
    setWording(words[randomIndex]);
  }, []);

  const handleSelectedPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const openSupportDialog = () => {
    supportDialogRef.current.showModal();
  };

  const closeSupportDialog = () => {
    supportDialogRef.current.close();
  };

  const openHowToBuymeacoffeeDialog = () => {
    if (selectedPlan) {
      howToBuymeacoffeeDialogRef.current.showModal();
    } else {
      alert('Please select a plan first');
    }
  };

  const closeHowToBuymeacoffeeDialog = () => {
    howToBuymeacoffeeDialogRef.current.close();
  };

  return (
    <>
      <dialog
        ref={dialogRef}
        className='w-full h-full max-w-lg max-h-full p-4 pb-12 bg-primary'
      >
        <div className='flex justify-between items-center'>
          <div className='font-bold text-white'>{title}</div>
          <button
            className='text-xl text-white'
            onClick={closeDialog}
          >
            🅧
          </button>
        </div>
        <div className='flex flex-col gap-2 items-center my-8'>
          <h1 className='text-xl italic text-center text-white mb-2'>
            <div className='text-transparent bg-clip-text bg-gradient-to-tr from-white to-accent'>
              {wording.map((word, i) => (
                <div key={i}>{word}</div>
              ))}
            </div>
            <br />
            <span className='font-bold'>Get Unlimited Access Now!</span>
          </h1>
          <ul className='space-y-3 bg-secondary rounded-2xl p-4 border border-tertiary w-full'>
            {offers.map((offer, i) => (
              <li
                key={i}
                className='flex items-start'
              >
                <svg
                  className='size-5 text-accent mr-2'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M5 13l4 4L19 7'
                  />
                </svg>
                <span className='text-white text-sm'>{offer}</span>
              </li>
            ))}
          </ul>
          <div className='border-b-4 border-accent w-1/12 mb-4' />
          <Plans
            selectedPlan={selectedPlan}
            handleSelectedPlan={handleSelectedPlan}
          />
        </div>
        <PayPalSubscriptionButton
          refreshUser={refreshUser}
          openHowToBuymeacoffeeDialog={openHowToBuymeacoffeeDialog}
        />
        <div className='flex justify-center items-center'>
          <button
            className='text-sm text-blue-600 mb-8 italic underline'
            onClick={openSupportDialog}
          >
            Need help?
          </button>
        </div>
        <Policy />
      </dialog>
      <Support
        dialogRef={supportDialogRef}
        closeDialog={closeSupportDialog}
      />
      <HowToBuymeacoffeeDialog
        dialogRef={howToBuymeacoffeeDialogRef}
        closeDialog={closeHowToBuymeacoffeeDialog}
        selectedPlan={selectedPlan}
      />
    </>
  );
};

export default PayDialog;
